import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { History, Plus, Sparkles, Eye, FileClock, ArrowRight } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-slate-100 text-slate-600",
  printing: "bg-blue-100 text-blue-700",
  framing: "bg-amber-100 text-amber-700",
  dispatched: "bg-emerald-100 text-emerald-700",
};

export function StatusBadge({ status, label }) {
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLE[status] || STATUS_STYLE.pending}`}>
      {label}
    </span>
  );
}

export default function MRDashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [draftCount, setDraftCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [previewId, setPreviewId] = useState(null);

  useEffect(() => {
    api.get("/requests").then(({ data }) => setRequests(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/requests?status=draft").then(({ data }) => setDraftCount(data.length)).catch(() => {});
  }, []);

  const recent = requests.slice(0, 20);
  const last7 = requests.filter((r) => new Date(r.created_at) > new Date(Date.now() - 7 * 86400000)).length;

  return (
    <Shell role="mr">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Dashboard</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
            Hello, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-slate-500 mt-1">Create a personalized Master of ICU certificate and track its status.</p>
        </div>
        <Link to="/mr/generate">
          <Button className="h-12 px-6 bg-brand-800 hover:bg-brand-700 gap-2">
            <Plus className="w-4 h-4" /> New Certificate
          </Button>
        </Link>
      </div>

      {/* Draft reminder banner */}
      {draftCount > 0 && (
        <Link to="/mr/drafts" className="block mb-6">
          <Card className="p-4 border-amber-300 bg-amber-50 hover:bg-amber-100/70 transition flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 grid place-items-center shrink-0">
              <FileClock className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-amber-900">
                You have {draftCount} draft{draftCount !== 1 ? "s" : ""} waiting to be submitted
              </div>
              <div className="text-sm text-amber-700">Review the preview and submit to send the request — drafts aren't sent until you submit them.</div>
            </div>
            <ArrowRight className="w-5 h-5 text-amber-600 shrink-0" />
          </Card>
        </Link>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Card className="p-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Certificates Created</div>
          <div className="font-display text-4xl font-extrabold mt-2">{requests.length}</div>
        </Card>
        <Link to="/mr/drafts" className="block">
          <Card className={`p-6 h-full transition hover:shadow-md ${draftCount > 0 ? "border-amber-300 bg-amber-50" : ""}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Drafts</div>
              <FileClock className={`w-4 h-4 ${draftCount > 0 ? "text-amber-600" : "text-slate-400"}`} />
            </div>
            <div className="font-display text-4xl font-extrabold mt-2">{draftCount}</div>
            <div className={`text-xs mt-1 ${draftCount > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>
              {draftCount > 0 ? "Tap to review & submit" : "No drafts pending"}
            </div>
          </Card>
        </Link>
        <Card className="p-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Last 7 Days</div>
          <div className="font-display text-4xl font-extrabold mt-2">{last7}</div>
        </Card>
        <Card className="p-6 bg-brand-800 text-white border-brand-800">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-300">Campaign</div>
          <div className="font-display text-lg font-extrabold mt-2 leading-tight">Master of ICU<br/>Certificate of Honour</div>
        </Card>
      </div>

      {/* Recent requests */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-slate-900">Recent Requests</h2>
        <Link to="/mr/history" className="text-sm font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
          View all <History className="w-4 h-4" />
        </Link>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : recent.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 grid place-items-center mb-3">
            <Sparkles className="w-6 h-6 text-slate-500" />
          </div>
          <div className="font-display text-lg font-bold text-slate-900 mb-1">No certificates yet</div>
          <div className="text-sm text-slate-500 mb-4">Create your first Master of ICU certificate now.</div>
          <Link to="/mr/generate"><Button>Create Certificate</Button></Link>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Request ID</th>
                  <th className="px-4 py-3">Doctor Name</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3 text-right">Preview</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900">{r.display_id}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{r.doctor_name}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} label={r.status_label} /></td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString()}{" "}
                      <span className="text-xs text-slate-400">
                        {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setPreviewId(r.id)}>
                        <Eye className="w-3.5 h-3.5" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Preview dialog (no download for MR) */}
      <Dialog open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Certificate Preview</DialogTitle>
          </DialogHeader>
          {previewId && (
            <img
              src={`${API}/requests/${previewId}/preview?t=${Date.now()}`}
              alt="Certificate preview"
              className="w-full rounded-md border border-slate-200"
            />
          )}
          <p className="text-xs text-slate-400 text-center">Preview only — the admin team handles printing &amp; dispatch.</p>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
